import React, { useEffect, useMemo, useState } from 'react';
import {
    Paper, Box, Grid, TextField, MenuItem, Typography, Button,
    FormControlLabel, Checkbox, Alert, Snackbar,
    Dialog, DialogTitle, DialogContent, DialogActions,
    RadioGroup, Radio, FormLabel, Stack, Divider,
    Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { apiFetch } from '../../utils/api';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { PayPalButtons } from '@paypal/react-paypal-js';

/* =========================
   Constants
========================= */

const STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME',
    'MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA',
    'RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const TYPE_OPTIONS = [
    { value: 'individual', label: 'Individual / Family' },
    { value: 'group',      label: 'Group (non-school)' },
    { value: 'school',     label: 'School Field Trip' },
    { value: 'rental',     label: 'Depot Rental — Event ($150/day)' }
];

/* Depot full-day event rental */
const RENTAL_PRICE_CENTS = 15000; // $150 flat per day
const EVENT_TYPE_OPTIONS = [
    'Class Reunion', 'Tea', 'Meeting', 'Shower', 'Other'
];

const SCHOOL_BLOCKS = [
    { value: 'AM', label: 'Morning (9:30–12:00)' },
    { value: 'PM', label: 'Afternoon (1:00–3:00)' }
];

// Always-grey threshold for calendar
const MIN_DATE_ISO = '2025-09-29';

const GROUP_SIZE_VALUES    = Array.from({ length: 99 }, (_, i) => i + 2);
const STUDENT_COUNT_VALUES = Array.from({ length: 80 }, (_, i) => i + 1);

const GRADE_OPTIONS = [
    'Kindergarten','1st Grade','2nd Grade','3rd Grade','4th Grade','5th Grade',
    '6th Grade','7th Grade','8th Grade','9th Grade','10th Grade','11th Grade','12th Grade','Other'
];

/* ======= Helpers (match server) ======= */
const pad = (n) => String(n).padStart(2, '0');
const toMin = (s) => { const [h,m]=s.split(':').map(Number); return h*60+m; };
const toHHMM = (m) => `${pad(Math.floor(m/60))}:${pad(m%60)}`;
const overlaps = (s,e,[bs,be]) => s < be && bs < e;
const pointIn = (t,[bs,be]) => t>=bs && t<be;
const add60 = (t) => toHHMM(toMin(t)+60);

const fmtLabel = (hhmm) => {
    const [h,m] = hhmm.split(':').map(Number);
    const ampm = h>=12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${pad(m)} ${ampm}`;
};

function isoToJSDate(iso){ if(!iso) return null; const [y,m,d]=iso.split('-').map(Number); return new Date(y,m-1,d); }
function jsDateToISO(d){ if(!d||Number.isNaN(d.getTime())) return ''; const u=new Date(Date.UTC(d.getFullYear(),d.getMonth(),d.getDate())); return u.toISOString().slice(0,10); }

/** Holiday closures applied every year by month-day (inclusive ranges). */
const HOLIDAY_CLOSURES_MMDD = [
    ['11-25', '11-29'], // Example: Thanksgiving week closure
];

function isHoliday(iso){
    if (!iso) return false;
    const md = iso.slice(5,10);
    return HOLIDAY_CLOSURES_MMDD.some(([start,end]) => start <= md && md <= (end || start));
}

/**
 * Client fallback of server hours (used if API data isn't available):
 * - Friday ..................... 10:00–15:00
 * - 2nd & 4th Saturday ......... 10:00–13:00
 * - Sunday ..................... 13:30–16:00
 * - Major holidays ............. CLOSED (e.g., Nov 25–29)
 * - All other days ............. CLOSED
 */
function museumHoursJS(iso){
    if (!iso) return { open:'00:00', close:'00:00', isClosed:true };
    if (isHoliday(iso)) return { open:'00:00', close:'00:00', isClosed:true };

    const d=new Date(iso+'T00:00:00'); const dow=d.getDay(); // 0=Sun ... 6=Sat
    let open='00:00', close='00:00', isClosed=true;

    if (dow===0){ // Sunday
        open='13:30'; close='16:00'; isClosed=false;
    } else if (dow===5){ // Friday
        open='10:00'; close='15:00'; isClosed=false;
    } else if (dow===6){ // Saturday: only 2nd & 4th
        const nth=Math.floor((d.getDate()-1)/7)+1; // 1..5
        if(nth===2||nth===4){ open='10:00'; close='13:00'; isClosed=false; }
    }
    return {open,close,isClosed};
}

function getTodayISO(){ const now=new Date(); const u=new Date(Date.UTC(now.getFullYear(),now.getMonth(),now.getDate())); return u.toISOString().slice(0,10); }

const AFTER_HOURS = '__AFTER_HOURS__';

/* ===========================================
   Component
=========================================== */
export default function BookingForm({ slot = null, onClear }) {
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

    /* Contact */
    const [first, setFirst] = useState('');
    const [last,  setLast]  = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');

    /* Visit & shared */
    const [type, setType] = useState('individual');
    const [date, setDate] = useState('');
    const [comments, setComments] = useState('');

    /* Individual */
    const [bookingTime, setBookingTime] = useState('');

    /* Group */
    const [groupSize, setGroupSize] = useState('');
    const [exclusive, setExclusive] = useState(false);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [groupTime, setGroupTime] = useState('');
    const afterHoursSelected = useMemo(
        () => exclusive && (startTime === AFTER_HOURS || endTime === AFTER_HOURS),
        [exclusive, startTime, endTime]
    );

    /* School */
    const [schoolName,setSchoolName]=useState('');
    const [grade,setGrade]=useState('');
    const [schoolState,setSchoolState]=useState('AL');
    const [city,setCity]=useState('');
    const [schoolBlock,setSchoolBlock]=useState('');
    const [numStudents, setNumStudents] = useState('');

    /* Depot rental (full-day event) */
    const [eventType, setEventType] = useState('');
    const [expectedGuests, setExpectedGuests] = useState('');

    /* UI / availability */
    const [busy] = useState(false);
    const [locked,setLocked]=useState(false);
    const [inlineMsg,setInlineMsg]=useState({ kind:'', text:'' });

    const [snack,setSnack]=useState({ open:false, kind:'success', text:'' });
    const showSnack = (kind,text)=>{ if(isPhone) setSnack({open:true,kind,text}); };

    // Coarse per-day status preloaded for a year (helps disable instantly)
    const [rangeDays, setRangeDays] = useState(() => new Map());

    // Options for the selected day
    const [timeOptions,setTimeOptions]=useState([]);
    const [dayStatus,setDayStatus]=useState('OPEN');
    const [groupStartOpts,setGroupStartOpts]=useState([]);
    const [groupEndBaseOpts,setGroupEndBaseOpts]=useState([]);

    // Month cache of disabled days for current visit type
    const [monthDisabledCache,setMonthDisabledCache]=useState(new Map());
    const typeKey = useMemo(()=> (type==='group' ? (exclusive?'group:exclusive':'group:shared') : type), [type,exclusive]);

    /* ---------- Prefetch coarse range once ---------- */
    useEffect(() => {
        const end = (d => { d.setFullYear(d.getFullYear()+1); return d.toISOString().slice(0,10); })(new Date(MIN_DATE_ISO+'T00:00:00'));
        apiFetch(`/api/bookings/availability-range?start=${MIN_DATE_ISO}&end=${end}`, { cache: 'no-store' })
            .then(r=>r.json())
            .then(data=>{
                const map=new Map();
                if(data?.days){
                    for (const [d,info] of Object.entries(data.days)) {
                        map.set(d,{ dayStatus: info?.dayStatus || 'OPEN', blocked: info?.blocked || [] });
                    }
                }
                setRangeDays(map);
            })
            .catch(()=>setRangeDays(new Map()));
    }, []);

    /* ---------- External calendar click prefill ---------- */
    useEffect(() => {
        const raw = slot?.start || slot?.dateTime || slot;
        if (!raw) return;
        if (typeof raw === 'string') {
            const safe = raw.includes('T') ? raw : raw.replace(' ', 'T');
            const d = safe.slice(0,10);
            const t = safe.slice(11,16);
            if (d) setDate(d);
            if (t && type === 'individual') setBookingTime(t);
        }
    }, [slot, type]);

    useEffect(() => {
        if (date) window.dispatchEvent(new CustomEvent('phs:calendar:navigate', { detail: { date } }));
    }, [date]);

    /* ---------- Prefetch one month at open / navigation ---------- */
    useEffect(() => {
        setMonthDisabledCache(new Map());
        const base = isoToJSDate(date) || isoToJSDate(MIN_DATE_ISO) || new Date();
        prefetchMonthDetailed(base);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeKey]);

    async function prefetchMonthDetailed(d) {
        const y=d.getFullYear(), m=d.getMonth();
        const key = `${typeKey}|${y}-${pad(m+1)}`;
        if (monthDisabledCache.get(key)) return;

        const lastDay = new Date(y,m+1,0).getDate();
        const isoList = [];
        for (let day=1; day<=lastDay; day++) {
            const iso = `${y}-${pad(m+1)}-${pad(day)}`;
            if (iso >= MIN_DATE_ISO) isoList.push(iso);
        }

        try {
            const results = await Promise.all(isoList.map(async iso => {
                const resp = await apiFetch(`/api/bookings/availability?date=${iso}`, { cache:'no-store' });
                if (!resp.ok) return { iso, a:null };
                const a = await resp.json();
                return { iso, a };
            }));

            const disabled = new Set();
            for (const { iso, a } of results) {
                if (!a) {
                    // Fallback: if server didn't return, mark closed days via local rules.
                    // Rentals aren't limited to public open days — only holiday closures apply.
                    if (typeKey === 'rental' ? isHoliday(iso) : museumHoursJS(iso).isClosed) disabled.add(iso);
                    continue;
                }

                // Depot rental: any non-holiday day works, but the ENTIRE day must be free of bookings.
                if (typeKey === 'rental') {
                    if (isHoliday(iso)) { disabled.add(iso); continue; }
                    if (a.dayStatus === 'BOOKED' || rangeDays.get(iso)?.dayStatus === 'BOOKED') { disabled.add(iso); continue; }
                    const anyBlocked = (a.blocked || []).length > 0 || (rangeDays.get(iso)?.blocked || []).length > 0;
                    if (anyBlocked) disabled.add(iso);
                    continue;
                }

                // Closed days: hard disable
                if (a.dayStatus === 'CLOSED' || rangeDays.get(iso)?.dayStatus === 'CLOSED') { disabled.add(iso); continue; }

                // Fully booked
                if (a.dayStatus === 'BOOKED' || rangeDays.get(iso)?.dayStatus === 'BOOKED') { disabled.add(iso); continue; }

                const openM = toMin(a.open), closeM = toMin(a.close);
                const blocked = (a.blocked||[]).map(r => [toMin(r.start), toMin(r.end)]);

                let found = false;
                if (typeKey === 'individual' || typeKey === 'group:shared') {
                    for (let m=openM; m<=closeM-60; m+=60) { const s=m, e=m+60; if (!blocked.some(r=>overlaps(s,e,r))) { found=true; break; } }
                } else if (typeKey === 'school') {
                    const am=[toMin('09:30'), toMin('12:00')], pm=[toMin('13:00'), toMin('15:00')];
                    const amFree = !blocked.some(r=>overlaps(am[0],am[1],r));
                    const pmFree = !blocked.some(r=>overlaps(pm[0],pm[1],r));
                    found = amFree || pmFree;
                } else { // group:exclusive
                    for (let m=openM; m<=closeM-30; m+=30) { const s=m, e=m+30; if (!blocked.some(r=>overlaps(s,e,r))) { found=true; break; } }
                }
                if (!found) disabled.add(iso);
            }

            setMonthDisabledCache(prev => {
                const map = new Map(prev);
                map.set(key, disabled);
                return map;
            });
        } catch {
            // If the entire month load fails, still disable all closed-by-rule days as a safety net.
            const lastDay = new Date(d.getFullYear(), d.getMonth()+1, 0).getDate();
            const fallback = new Set();
            for (let day=1; day<=lastDay; day++) {
                const iso = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(day)}`;
                const closedByRule = typeKey === 'rental' ? isHoliday(iso) : museumHoursJS(iso).isClosed;
                if (iso >= MIN_DATE_ISO && closedByRule) fallback.add(iso);
            }
            setMonthDisabledCache(prev => {
                const map = new Map(prev);
                map.set(`${typeKey}|${d.getFullYear()}-${pad(d.getMonth()+1)}`, fallback);
                return map;
            });
        }
    }

    // Disable rules used by DatePicker and onChange guard
    const shouldDisableISO = (iso) => {
        if (!iso) return true;

        // Always gray before MIN_DATE_ISO
        if (iso < MIN_DATE_ISO) return true;

        // After we reach MIN_DATE_ISO, also gray out dates before "today"
        const tISO = getTodayISO();
        if (tISO >= MIN_DATE_ISO && iso < tISO) return true;

        // Block if closed by local rules.
        // Rentals may book any day (open to the public or not); only holiday closures apply.
        if (typeKey === 'rental' ? isHoliday(iso) : museumHoursJS(iso).isClosed) return true;

        // Fully booked or closed (from coarse range or month cache)
        const monthKey = `${typeKey}|${iso.slice(0,7)}`;
        if (monthDisabledCache.get(monthKey)?.has(iso)) return true;

        const coarse = rangeDays.get(iso)?.dayStatus;
        if (coarse === 'BOOKED') return true;
        if (coarse === 'CLOSED' && typeKey !== 'rental') return true;

        // Rental needs the entire day free of existing bookings
        if (typeKey === 'rental' && (rangeDays.get(iso)?.blocked?.length || 0) > 0) return true;

        return false;
    };

    /* ---------- Load selected day options (robust with fallback) ---------- */
    useEffect(() => {
        let cancelled = false;

        async function loadDay() {
            if (!date) { setTimeOptions([]); setGroupStartOpts([]); setGroupEndBaseOpts([]); setDayStatus('OPEN'); return; }
            if (date < MIN_DATE_ISO) { setDate(MIN_DATE_ISO); return; }

            // Start with safe defaults so we always have *some* times
            let { open, close, isClosed: fallbackClosed } = museumHoursJS(date);
            let blockedRanges = [];
            let serverDayStatus = fallbackClosed ? 'CLOSED' : 'OPEN';

            try {
                const res = await apiFetch(`/api/bookings/availability?date=${date}`, { cache:'no-store' });
                if (res.ok) {
                    const a = await res.json();
                    if (a?.open && a?.close) { open = a.open; close = a.close; }
                    if (Array.isArray(a?.blocked)) blockedRanges = a.blocked.map(r => [toMin(r.start), toMin(r.end)]);
                    serverDayStatus = a?.dayStatus || serverDayStatus;
                }
            } catch { /* leave fallback values */ }

            // If month prefetch already gave us blocks, use them on top
            if (blockedRanges.length === 0) {
                const rd = rangeDays.get(date);
                if (rd?.blocked?.length) {
                    blockedRanges = rd.blocked.map(r => [toMin(r.start), toMin(r.end)]);
                }
            }

            const openM  = toMin(open), closeM = toMin(close);

            // Individual/shared base options: every hour
            const oneHourBase = [];
            for (let m=openM; m<=closeM-60; m+=60) oneHourBase.push(toHHMM(m));

            // 30-minute grid for group exclusives
            const step = 30;
            const startList = []; const endList = [];
            for (let m=openM; m<=closeM-step; m+=step) startList.push(toHHMM(m));
            for (let m=openM+step; m<=closeM; m+=step) endList.push(toHHMM(m));
            const isPointBlocked = (t)=>blockedRanges.some(r=>pointIn(toMin(t),r));

            // Build options
            const indivOpts = oneHourBase.map(t => {
                const s = toMin(t), e = s + 60;
                const isBlocked = blockedRanges.some(r => overlaps(s,e,r));
                return { value:t, label:fmtLabel(t), disabled:isBlocked };
            });

            const startOpts = startList.map(t => ({ value:t, label:fmtLabel(t), disabled:isPointBlocked(t) }));
            const endBase   = endList.map(t => ({ value:t, label:fmtLabel(t), disabled:isPointBlocked(t) }));

            if (cancelled) return;

            setTimeOptions(indivOpts);
            setGroupStartOpts(startOpts);
            setGroupEndBaseOpts(endBase);

            // Derive day status for current type
            let hasWindow = true;
            if (typeKey === 'individual' || typeKey === 'group:shared') {
                hasWindow = indivOpts.some(o => !o.disabled);
            } else if (typeKey === 'school') {
                const am=[toMin('09:30'),toMin('12:00')], pm=[toMin('13:00'),toMin('15:00')];
                const amFree=!blockedRanges.some(r=>overlaps(am[0],am[1],r));
                const pmFree=!blockedRanges.some(r=>overlaps(pm[0],pm[1],r));
                hasWindow = amFree || pmFree;
            } else if (typeKey === 'rental') {
                // Full-day rental: available only when no other booking exists on that day
                hasWindow = blockedRanges.length === 0;
            } else {
                hasWindow = startOpts.some(o=>!o.disabled);
            }

            // Rentals ignore "closed to the public" — only holiday closures count as CLOSED for them.
            const closedForThisType = typeKey === 'rental' ? isHoliday(date) : serverDayStatus === 'CLOSED';

            const newDayStatus =
                closedForThisType
                    ? 'CLOSED'
                    : (serverDayStatus === 'BOOKED' || !hasWindow)
                        ? 'BOOKED'
                        : (blockedRanges.length ? 'PARTIAL' : 'OPEN');
            setDayStatus(newDayStatus);

            // Keep calendar cache in sync so future open shows disabled immediately
            if (newDayStatus === 'BOOKED' || newDayStatus === 'CLOSED') {
                setMonthDisabledCache(prev => {
                    const key = `${typeKey}|${date.slice(0,7)}`;
                    const map = new Map(prev);
                    const set = new Set(map.get(key) || []);
                    set.add(date);
                    map.set(key,set);
                    return map;
                });
            }

            // Clear invalid selections when availability changes
            if (bookingTime && indivOpts.some(o=>o.value===bookingTime && o.disabled)) setBookingTime('');
            if (groupTime && indivOpts.some(o=>o.value===groupTime && o.disabled)) setGroupTime('');
            if (startTime && startOpts.some(o=>o.value===startTime && o.disabled)) setStartTime('');
            if (endTime) {
                const disableEnd = (t) => {
                    if (t === AFTER_HOURS) return false;
                    const e = toMin(t);
                    if (startTime && startTime !== AFTER_HOURS && e <= toMin(startTime)) return true;
                    if (startTime && startTime !== AFTER_HOURS && blockedRanges.some(r => overlaps(toMin(startTime), e, r))) return true;
                    return endBase.some(o => o.value === t && o.disabled);
                };
                if (disableEnd(endTime)) setEndTime('');
            }
        }

        loadDay();
        return ()=>{ cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, typeKey]);

    const groupEndOpts = useMemo(() => {
        if (!groupEndBaseOpts.length) return [{ value: AFTER_HOURS, label: 'After Hours', disabled: false }];
        if (startTime === AFTER_HOURS) return [{ value: AFTER_HOURS, label: 'After Hours', disabled: false }];
        const base = groupEndBaseOpts
            .filter(o => !o.disabled && (!startTime || toMin(o.value) > toMin(startTime)))
            .map(o => ({ ...o, disabled: false }));
        base.push({ value: AFTER_HOURS, label: 'After Hours', disabled: false });
        return base;
    }, [groupEndBaseOpts, startTime]);

    useEffect(()=>{ if(exclusive && startTime===AFTER_HOURS) setEndTime(AFTER_HOURS); }, [exclusive,startTime]);

    /* ---------- Submit (opens payment dialog) ---------- */
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('paypal');
    const [indivParty, setIndivParty] = useState('1'); // TEXT INPUT in dialog
    const [paymentBusy, setPaymentBusy] = useState(false);

    // Validation for individual-party input (only 1..150, digits only)
    const indivPartyValid = useMemo(() => {
        if (type !== 'individual') return true;
        const s = (indivParty || '').trim();
        if (!/^\d+$/.test(s)) return false;
        const n = parseInt(s, 10);
        return n >= 1 && n <= 150;
    }, [type, indivParty]);
    const indivPartyValue = indivPartyValid ? parseInt(indivParty, 10) : 0;

    // Members count (individuals only)
    const [membersCount, setMembersCount] = useState(0);

    // People/amount math ($5 each). If individual count is invalid, treat as 0 to block payment.
    const peopleCount = useMemo(() => {
        if (type === 'school') return Number(numStudents) || 0;
        if (type === 'group')  return Number(groupSize) || 0;
        if (type === 'rental') return Number(expectedGuests) || 0; // optional estimate
        return indivPartyValue; // 0 when invalid
    }, [type, numStudents, groupSize, expectedGuests, indivPartyValue]);

    // Clamp members to current party size; clear when not individual
    useEffect(() => { setMembersCount((prev) => Math.min(prev, peopleCount)); }, [peopleCount]);
    useEffect(() => { if (type !== 'individual') setMembersCount(0); }, [type]);

    const effectiveMembers = useMemo(
        () => (type === 'individual' ? Math.max(0, Math.min(Number(membersCount)||0, peopleCount)) : 0),
        [type, membersCount, peopleCount]
    );
    const payingCount = useMemo(() => Math.max(0, peopleCount - effectiveMembers), [peopleCount, effectiveMembers]);
    // Depot rental is a flat $150/day; everything else is $5 per paying person.
    const amountCents = useMemo(
        () => (type === 'rental' ? RENTAL_PRICE_CENTS : payingCount * 500),
        [type, payingCount]
    );

    const canSubmit = useMemo(() => {
        if (locked) return false;
        const base = first && last && email && phone && date;
        if (!base) return false;

        if (type === 'individual') { if (dayStatus === 'BOOKED' || dayStatus === 'CLOSED') return false; return Boolean(bookingTime); }
        if (type === 'group') { if (!groupSize) return false; if (exclusive) return Boolean((startTime && endTime) || afterHoursSelected); if (dayStatus==='BOOKED' || dayStatus==='CLOSED') return false; return Boolean(groupTime); }
        if (type === 'school') { return Boolean(schoolName && grade && city && schoolState && schoolBlock && numStudents); }
        if (type === 'rental') { if (dayStatus === 'BOOKED' || dayStatus === 'CLOSED') return false; return Boolean(eventType); }
        return false;
    }, [locked,first,last,email,phone,date,type,bookingTime,groupSize,startTime,endTime,groupTime,exclusive,afterHoursSelected,schoolName,grade,city,schoolState,schoolBlock,numStudents,eventType,dayStatus]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;

        const payload = {
            first_name:first.trim(),
            last_name:last.trim(),
            email:email.trim(),
            phone:phone.trim(),
            type,
            booking_date: date,
            comments: (comments||'').trim() || null
        };

        if (type === 'individual') {
            payload.booking_time = bookingTime;
        } else if (type === 'group') {
            payload.group_size = groupSize ? Number(groupSize) : null;
            payload.exclusive  = Boolean(exclusive);
            if (exclusive) {
                if (afterHoursSelected) { payload.after_hours = true; payload.start_time = null; payload.end_time = null; }
                else { payload.start_time = startTime || null; payload.end_time = endTime || null; }
            } else {
                payload.start_time = groupTime || null;
                payload.end_time   = groupTime ? add60(groupTime) : null;
            }
        } else if (type === 'rental') {
            payload.event_type = eventType;
            payload.expected_guests = expectedGuests ? Number(expectedGuests) : null;
            payload.full_day = true; // rental reserves the entire Depot for the whole day
        } else {
            payload.school_name = schoolName.trim();
            payload.grade = grade;
            payload.state = schoolState;
            payload.city  = city.trim();
            payload.school_block = schoolBlock;
            payload.number_of_students = numStudents ? Number(numStudents) : 0;
        }

        setPendingPayload(payload);
        setPaymentMethod('paypal'); // default; PayPal rendered only when valid party count
        setPaymentOpen(true);
    };

    /* ---------- Finalize booking (send to server) ---------- */
    async function finalizeBooking(method, { paypalOrderId = '' } = {}) {
        if (!pendingPayload) return;
        setPaymentBusy(true);
        setInlineMsg({ kind:'', text:'' });

        const peopleTotal = peopleCount; // keep stable read
        const amount = amountCents;

        const payload = {
            ...pendingPayload,
            payment_method: method,                         // 'paypal' | 'cash' | 'check' | 'comp'
            paypal_order_id: method === 'paypal' ? paypalOrderId : '',
            people_count: peopleTotal,
            amount_cents: amount,
        };

        try {
            const res = await apiFetch('/api/bookings', {
                method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload)
            });
            const data = await res.json().catch(()=> ({}));

            if (!res.ok) {
                const msg = data?.error ? (Array.isArray(data.error) ? data.error.join(', ') : data.error) : 'Unable to submit booking right now.';
                if (res.status === 409) {
                    await (async () => {
                        const r = await apiFetch(`/api/bookings/availability?date=${pendingPayload.booking_date}`, { cache:'no-store' });
                        const a = await r.json();
                        setDayStatus(a.dayStatus || 'PARTIAL');
                        setTimeOptions([]); // repopulates after user taps the date
                    })();
                    setPaymentOpen(false);
                }
                setInlineMsg({ kind:'error', text: msg });
                showSnack('error', msg);
            } else {
                const okMsg =
                    data?.message ||
                    (method === 'paypal'
                        ? 'Booking created and confirmed via PayPal.'
                        : method === 'comp'
                            ? 'Booking created and confirmed — no payment due.'
                            : 'Booking created; we’ll confirm after your payment is received.');
                setInlineMsg({ kind:'success', text: okMsg });
                showSnack('success', 'Booking submitted!');
                setLocked(true);
                setPaymentOpen(false);
                if (onClear) onClear();
            }
        } catch {
            setInlineMsg({ kind:'error', text:'Network error. Please try again.' });
            showSnack('error','Network error—try again.');
        } finally {
            setPaymentBusy(false);
        }
    }

    /* ---------- UI ---------- */
    const forceFullWidthSX = { width:'100%', '& .MuiFormControl-root': { width:'100%' } };

    return (
        <>
            <Paper
                elevation={1}
                component="form"
                onSubmit={handleSubmit}
                sx={{ p:{ xs:2.5, md:3 }, borderRadius:2, maxWidth:960, mx:'auto' }}
                noValidate
                aria-labelledby="book-a-visit-title"
            >
                <Typography id="book-a-visit-title" variant="h6" sx={{ fontWeight:700, mb:1.25 }}>
                    Book a Visit
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose your visit type and tell us how to reach you. We’ll confirm by email.
                </Typography>

                {inlineMsg.text && !isPhone && (
                    <Alert role="status" severity={inlineMsg.kind === 'error' ? 'error' : 'success'} sx={{ mb: 2 }}>
                        {inlineMsg.text}
                    </Alert>
                )}

                {/* Contact row */}
                <Grid container spacing={{ xs:2, md:2.5 }}>
                    <Grid item xs={12} md={4}>
                        <TextField label="First name" value={first} onChange={e=>setFirst(e.target.value)} required fullWidth disabled={locked} autoComplete="given-name" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField label="Last name" value={last} onChange={e=>setLast(e.target.value)} required fullWidth disabled={locked} autoComplete="family-name" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField label="Phone" value={phone} onChange={e=>setPhone(e.target.value)} required fullWidth disabled={locked} autoComplete="tel" />
                    </Grid>
                    {/* Email now full width on md+ */}
                    <Grid item xs={12} md={12}>
                        <TextField label="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required fullWidth disabled={locked} autoComplete="email" />
                    </Grid>
                </Grid>

                {/* Type + Date row */}
                <Grid container spacing={{ xs:2, md:2.5 }} sx={{ mt:2 }}>
                    <Grid item xs={12} sm={6} md={5}>
                        <TextField select label="Visit type" value={type} onChange={e=>setType(e.target.value)} required fullWidth disabled={locked}>
                            {TYPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Tooltip title="Use the calendar icon to pick a date">
                                <Box>
                                    <DatePicker
                                        label="Date"
                                        value={isoToJSDate(date)}
                                        disabled={locked}
                                        onOpen={() => {
                                            if (locked) return;
                                            const base = isoToJSDate(date) || isoToJSDate(MIN_DATE_ISO) || new Date();
                                            prefetchMonthDetailed(base);
                                        }}
                                        onMonthChange={(d)=>{
                                            if (locked) return;
                                            prefetchMonthDetailed(d);
                                        }}
                                        onChange={(newVal)=>{
                                            if (locked) return;
                                            const iso=jsDateToISO(newVal);
                                            if(!iso) return;
                                            if (shouldDisableISO(iso)) return;
                                            setDate(iso);
                                        }}
                                        minDate={isoToJSDate(MIN_DATE_ISO)}
                                        shouldDisableDate={(d)=>shouldDisableISO(jsDateToISO(d))}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                fullWidth
                                                disabled={locked}
                                                inputProps={{
                                                    ...(params.inputProps||{}),
                                                    readOnly: true,
                                                    inputMode: 'none',
                                                    'aria-readonly': true,
                                                    onBeforeInput: (e) => e.preventDefault(),
                                                    onKeyDown:     (e) => e.preventDefault(),
                                                    onPaste:       (e) => e.preventDefault(),
                                                    onDrop:        (e) => e.preventDefault(),
                                                    onMouseDown:   (e) => e.preventDefault(),
                                                    onClick:       (e) => e.preventDefault(),
                                                }}
                                                sx={{ '& input': { caretColor: 'transparent', userSelect: 'none' } }}
                                                helperText={
                                                    date
                                                        ? (dayStatus==='CLOSED'
                                                            ? 'The museum is closed on this day.'
                                                            : (dayStatus==='BOOKED' ? 'Fully booked — choose another date.' : null))
                                                        : null
                                                }
                                                FormHelperTextProps={{ sx:{ mt:0.5 } }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Tooltip>
                        </LocalizationProvider>
                    </Grid>
                </Grid>

                {date && dayStatus==='CLOSED' && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                        The museum is closed on this day. Please choose another date.
                    </Alert>
                )}
                {date && dayStatus==='BOOKED' && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                        {type === 'rental'
                            ? 'This date already has a booking, so the Depot can’t be rented that day. Please choose another date.'
                            : 'This date is fully booked. Please choose another date.'}
                    </Alert>
                )}

                {/* Individual — Time now half width on md+ */}
                {type === 'individual' && (
                    <Grid container spacing={{ xs:2, md:2.5 }} sx={{ mt:{ xs:2, sm:1 } }}>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                select
                                label="Time"
                                value={bookingTime}
                                onChange={(e)=>setBookingTime(e.target.value)}
                                required
                                fullWidth
                                disabled={locked || !date || dayStatus==='BOOKED' || dayStatus==='CLOSED'}
                                helperText={!date ? 'Choose a date first.' : (dayStatus==='CLOSED' ? 'Closed this day.' : (dayStatus==='BOOKED' ? 'No times left on this date.' : 'Select a start time'))}
                                FormHelperTextProps={{ sx:{ mt:0.5 } }}
                            >
                                {timeOptions.map(o => (
                                    <MenuItem key={o.value} value={o.value} disabled={o.disabled}>
                                        {o.label}{o.disabled ? ' — unavailable' : ''}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                )}

                {/* Group */}
                {type === 'group' && (
                    <>
                        <Grid container spacing={{ xs:2, md:2.5 }} sx={{ mt:2 }}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Checkbox checked={exclusive} onChange={e=>{ setExclusive(e.target.checked); setStartTime(''); setEndTime(''); setGroupTime(''); }} disabled={locked} />}
                                    label="Reserve Entire Depot"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} sx={forceFullWidthSX}>
                                <TextField select label="Group size" value={groupSize} onChange={e=>setGroupSize(e.target.value)} required fullWidth disabled={locked}>
                                    {GROUP_SIZE_VALUES.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>

                        {!exclusive && (
                            <Grid container spacing={{ xs:2, md:2.5 }} sx={{ mt:{ xs:2, sm:1 } }}>
                                <Grid item xs={12} sm={6} md={6}>
                                    <TextField
                                        select label="Time" value={groupTime} onChange={(e)=>setGroupTime(e.target.value)} required fullWidth
                                        disabled={locked || !date || dayStatus==='BOOKED' || dayStatus==='CLOSED'}
                                        helperText={!date ? 'Choose a date first.' : (dayStatus==='CLOSED' ? 'Closed this day.' : (dayStatus==='BOOKED' ? 'No times left on this date.' : 'Select a start time'))}
                                        FormHelperTextProps={{ sx:{ mt:0.5 } }}
                                    >
                                        {timeOptions.map(o => (
                                            <MenuItem key={o.value} value={o.value} disabled={o.disabled}>{o.label}{o.disabled?' — unavailable':''}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </Grid>
                        )}

                        {exclusive && (
                            <Grid container spacing={{ xs:2, md:2.5 }} sx={{ mt:{ xs:2, sm:1 } }}>
                                <Grid item xs={12} sm={6} md={4} sx={forceFullWidthSX}>
                                    <TextField
                                        select label="Start time" value={startTime}
                                        onChange={(e)=>{ setStartTime(e.target.value); if(e.target.value!==AFTER_HOURS) setEndTime(''); }}
                                        required fullWidth disabled={locked || !date || dayStatus==='BOOKED' || dayStatus==='CLOSED'}
                                    >
                                        {groupStartOpts.map(o => <MenuItem key={o.value} value={o.value} disabled={o.disabled}>{o.label}{o.disabled?' — unavailable':''}</MenuItem>)}
                                        <MenuItem value={AFTER_HOURS}>After Hours</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} sx={forceFullWidthSX}>
                                    <TextField
                                        select label="End time" value={endTime} onChange={(e)=>setEndTime(e.target.value)} required fullWidth
                                        disabled={locked || !date || dayStatus==='BOOKED' || dayStatus==='CLOSED' || !startTime || startTime===AFTER_HOURS}
                                        helperText={startTime ? (startTime===AFTER_HOURS ? 'After Hours selected — we’ll coordinate times.' : 'Choose a time after start.') : 'Choose start time first.'}
                                    >
                                        {groupEndOpts.map(o => <MenuItem key={o.value} value={o.value} disabled={o.disabled}>{o.label}</MenuItem>)}
                                    </TextField>
                                </Grid>
                            </Grid>
                        )}
                    </>
                )}

                {/* School */}
                {type === 'school' && (
                    <>
                        <Grid container spacing={{ xs:2, md:2.5 }} sx={{ mt:2 }}>
                            <Grid item xs={12} sx={forceFullWidthSX}>
                                <TextField label="School name" value={schoolName} onChange={e=>setSchoolName(e.target.value)} required fullWidth disabled={locked} />
                            </Grid>
                        </Grid>
                        <Grid container spacing={{ xs:2, md:2.5 }} sx={{ mt:1 }}>
                            <Grid item xs={12} sm={6} md={4} sx={forceFullWidthSX}>
                                <TextField select label="Grade" value={grade} onChange={e=>setGrade(e.target.value)} required fullWidth disabled={locked}>
                                    {GRADE_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} sx={forceFullWidthSX}>
                                <TextField select label="Number of students" value={numStudents} onChange={e=>setNumStudents(e.target.value)} required fullWidth disabled={locked}>
                                    {STUDENT_COUNT_VALUES.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>
                        <Grid container spacing={{ xs:2, md:2.5 }} sx={{ mt:1 }}>
                            <Grid item xs={12} sm={4} md={2} sx={forceFullWidthSX}>
                                <TextField select label="State" value={schoolState} onChange={e=>setSchoolState(e.target.value)} required fullWidth disabled={locked}>
                                    {STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} sx={forceFullWidthSX}>
                                <TextField label="City" value={city} onChange={e=>setCity(e.target.value)} required fullWidth disabled={locked} />
                            </Grid>
                        </Grid>
                        <Grid container spacing={{ xs:2, md:2.5 }} sx={{ mt:1 }}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    select
                                    label="Time"
                                    value={schoolBlock}
                                    onChange={(e)=>setSchoolBlock(e.target.value)}
                                    required
                                    fullWidth
                                    disabled={locked || !date}
                                    helperText={!date ? 'Pick a date first to enable time.' : 'Select a time block'}
                                    FormHelperTextProps={{ sx:{ mt:0.5 } }}
                                >
                                    {SCHOOL_BLOCKS.map(b => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>
                    </>
                )}

                {/* Depot Rental (full-day event) */}
                {type === 'rental' && (
                    <>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Depot rental is <b>$150 for the day</b> and reserves the entire Depot for your event
                            (class reunions, teas, meetings, showers, and more). We’ll contact you to coordinate
                            access and setup times.
                        </Alert>
                        <Grid container spacing={{ xs:2, md:2.5 }} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={6} md={5} sx={forceFullWidthSX}>
                                <TextField
                                    select
                                    label="Event type"
                                    value={eventType}
                                    onChange={(e)=>setEventType(e.target.value)}
                                    required
                                    fullWidth
                                    disabled={locked}
                                >
                                    {EVENT_TYPE_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} sx={forceFullWidthSX}>
                                <TextField
                                    select
                                    label="Expected guests (optional)"
                                    value={expectedGuests}
                                    onChange={(e)=>setExpectedGuests(e.target.value)}
                                    fullWidth
                                    disabled={locked}
                                    helperText="A rough estimate helps us prepare."
                                    FormHelperTextProps={{ sx:{ mt:0.5 } }}
                                >
                                    <MenuItem value="">Not sure yet</MenuItem>
                                    {GROUP_SIZE_VALUES.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>
                    </>
                )}

                <TextField
                    label="Comments and Questions"
                    value={comments}
                    onChange={(e)=>setComments(e.target.value)}
                    fullWidth multiline minRows={3} sx={{ mt:3 }} disabled={locked}
                />

                <Box sx={{ display:'flex', gap:1.5, mt:2.5 }}>
                    <Button type="submit" variant="contained" disabled={!canSubmit || busy}>{busy ? 'Booking…' : (locked ? 'Booked' : 'Book')}</Button>
                    {slot && <Button onClick={onClear} disabled={locked}>Clear selected time</Button>}
                </Box>
            </Paper>

            <Snackbar
                open={snack.open}
                autoHideDuration={2400}
                onClose={()=>setSnack(s=>({ ...s, open:false }))}
                anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
                sx={{ display:{ xs:'block', sm:'none' } }}
            >
                <Alert onClose={()=>setSnack(s=>({ ...s, open:false }))} severity={snack.kind==='error' ? 'error' : 'success'} variant="filled" sx={{ width:'100%' }}>
                    {snack.text}
                </Alert>
            </Snackbar>

            {/* Payment Dialog */}
            <Dialog open={paymentOpen} onClose={() => (paymentBusy ? null : setPaymentOpen(false))} aria-labelledby="payment-title" fullWidth maxWidth="sm">
                <DialogTitle id="payment-title">Payment</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {type === 'individual' && (
                            <TextField
                                label="Number in your party"
                                value={indivParty}
                                onChange={(e) => {
                                    // Accept digits only, keep at most 3 chars (to cover 150)
                                    const cleaned = (e.target.value || '').replace(/[^\d]/g, '').slice(0, 3);
                                    setIndivParty(cleaned);
                                }}
                                inputProps={{ inputMode:'numeric', pattern:'[0-9]*', maxLength:3 }}
                                error={indivParty !== '' && !indivPartyValid}
                                helperText={
                                    indivParty === ''
                                        ? 'Enter 1–150'
                                        : (indivPartyValid ? 'Members are free; adjust below if needed.' : 'Please enter a whole number between 1 and 150.')
                                }
                                fullWidth
                            />
                        )}

                        {/* Members selector (individual only); disabled until party is valid */}
                        {type === 'individual' && (
                            <TextField
                                select
                                label="Number of society members"
                                value={membersCount}
                                onChange={(e)=>setMembersCount(Number(e.target.value || 0))}
                                helperText="Members are free; we subtract them from the total."
                                disabled={!indivPartyValid}
                            >
                                {Array.from({ length: (peopleCount || 0) + 1 }, (_, i) => i).map(i => (
                                    <MenuItem key={i} value={i}>{i}</MenuItem>
                                ))}
                            </TextField>
                        )}

                        <Divider />

                        {/* Total & payment method */}
                        {(type !== 'individual' || indivPartyValid) ? (
                            <>
                                {amountCents === 0 ? (
                                    <Alert severity="success">No payment due — all attendees are members. Click <b>Confirm Booking</b> to finish.</Alert>
                                ) : (
                                    <>
                                        <Box>
                                            <FormLabel id="payment-method-label">Choose a method</FormLabel>
                                            <RadioGroup row aria-labelledby="payment-method-label" value={paymentMethod} onChange={(e)=>setPaymentMethod(e.target.value)}>
                                                <FormControlLabel value="paypal" control={<Radio />} label="PayPal" />
                                                <FormControlLabel value="cash"   control={<Radio />} label="Cash" />
                                                <FormControlLabel value="check"  control={<Radio />} label="Check" />
                                            </RadioGroup>
                                        </Box>

                                        <Typography variant="body2" sx={{ fontWeight:600 }}>
                                            {type === 'rental'
                                                ? <>Total due: ${ (amountCents/100).toFixed(2) } (Depot rental — full day)</>
                                                : <>Total due: ${ (amountCents/100).toFixed(2) } ({payingCount} paying × $5 after members)</>}
                                        </Typography>

                                        {paymentMethod === 'paypal' ? (
                                            <PayPalButtons
                                                style={{ layout:'horizontal' }}
                                                forceReRender={[amountCents]}
                                                createOrder={(data,actions) => actions.order.create({
                                                    purchase_units:[{
                                                        amount:{ value:(amountCents/100).toFixed(2) },
                                                        description: type === 'rental' ? 'Depot full-day event rental' : 'Museum visit booking'
                                                    }]
                                                })}
                                                onApprove={async (data, actions) => {
                                                    try {
                                                        await actions.order.capture();
                                                        await finalizeBooking('paypal', { paypalOrderId: data.orderID });
                                                    } catch {
                                                        setInlineMsg({ kind:'error', text:'PayPal approval failed. Please try again.' });
                                                        showSnack('error','PayPal approval failed.');
                                                    }
                                                }}
                                                onError={() => {
                                                    setInlineMsg({ kind:'error', text:'PayPal error. Please try again or choose a different method.' });
                                                    showSnack('error','PayPal error.');
                                                }}
                                            />
                                        ) : (
                                            <Alert severity="info" role="status">
                                                You can bring your {paymentMethod} the day of your visit. We’ll include payment details in the confirmation email.
                                            </Alert>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <Alert severity="warning">
                                Enter a valid number of visitors (1–150) to continue.
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={()=>setPaymentOpen(false)} disabled={paymentBusy}>Cancel</Button>
                    {/* Block manual confirmation while invalid (individual) */}
                    {(amountCents === 0 || paymentMethod==='cash' || paymentMethod==='check') && (
                        <Button
                            onClick={()=>finalizeBooking(amountCents === 0 ? 'comp' : paymentMethod)}
                            variant="contained"
                            disabled={paymentBusy || (type === 'individual' && !indivPartyValid)}
                        >
                            {paymentBusy ? 'Saving…' : 'Confirm Booking'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
